import { useCallback, useEffect, useState, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  BrowserNotSupportedIcon,
  ChartIcon,
  HighlightIcon,
  HighlightOutlinedIcon,
  IconButton,
  Paper,
  Typography,
  ZoomInSearchIcon,
} from '@/ui';
import { useDetailsCheckedFeatures, useDetailsStoreActions } from '@/core/stores/store-interface-and-intial-values/feature-info-state';
import { useMapStoreActions } from '@/core/stores/store-interface-and-intial-values/map-state';
import {
  useGeochartConfigs,
  useGeochartLayerDataArrayBatch,
  useGeochartStoreActions,
} from '@/core/stores/store-interface-and-intial-values/geochart-state';
import { useNavigateToTab } from '@/core/components/common/hooks/use-navigate-to-tab';
import { logger } from '@/core/utils/logger';
import { GeoUtilities } from '@/geo/utils/utilities';
import type { TypeFeatureInfoEntry, TypeFieldEntry } from '@/api/types/map-schema-types';
import { FeatureInfoTable } from './feature-info-table';
import { getSxClasses } from './details-style';

interface FeatureInfoProps {
  feature: TypeFeatureInfoEntry;
}

interface FeatureHeaderProps {
  iconSrc: string | undefined;
  name: string;
  hasGeometry: boolean;
  hasGeochart: boolean;
  checked: boolean;
  onCheckChange: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onZoomIn: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onGeochart: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

// Constants outside component to prevent recreating every render
const HEADER_STYLES = {
  container: {
    p: '0 16px 10px 16px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
} as const;

const PAPER_STYLES = {
  boxShadow: 'none',
  border: 'none',
  paddingTop: '0.5rem',
} as const;

const TYPOGRAPHY_STYLES = {
  display: 'inline-block',
} as const;

const ZOOM_PADDING = [5, 5, 5, 5];
const ZOOM_MAX_LEVEL = 17;
const EXTENT_BUFFER = 1000;

// Extracted Header Component
const FeatureHeader = memo(function FeatureHeader({
  iconSrc,
  name,
  hasGeometry,
  hasGeochart,
  checked,
  onCheckChange,
  onZoomIn,
  onGeochart,
}: FeatureHeaderProps) {
  // Hooks
  const { t } = useTranslation();
  const theme = useTheme();
  const sxClasses = useMemo(() => getSxClasses(theme), [theme]);

  return (
    <Box sx={HEADER_STYLES.container}>
      <Box sx={sxClasses.flexBoxAlignCenter}>
        {iconSrc ? (
          <Box component="img" src={iconSrc} alt={name} className="layer-icon" />
        ) : (
          <Box component="div" aria-label={name} className="layer-icon">
            <BrowserNotSupportedIcon />
          </Box>
        )}
        <Typography sx={TYPOGRAPHY_STYLES} component="div">
          {iconSrc ? (
            (name ?? t('details.nullValue'))
          ) : (
            <>
              {`(${name}) `}
              <Typography component="span" sx={{ fontStyle: 'italic' }}>
                {t('details.notSymbolized')}
              </Typography>
            </>
          )}
        </Typography>
      </Box>
      <Box
        sx={{
          ...sxClasses.flexBoxAlignCenter,
          [theme.breakpoints.down('sm')]: { display: 'none' },
        }}
      >
        {hasGeochart && (
          <IconButton
            color="primary"
            aria-label={t('details.selectLayerAndScrollChart')}
            tooltipPlacement="top"
            onClick={onGeochart}
            className="buttonOutline"
            size="small"
          >
            <ChartIcon />
          </IconButton>
        )}
        <IconButton
          aria-label={t('details.keepFeatureSelected')}
          tooltipPlacement="top"
          disabled={!hasGeometry}
          onClick={onCheckChange}
          className="buttonOutline"
          size="small"
        >
          {checked ? <HighlightIcon /> : <HighlightOutlinedIcon />}
        </IconButton>
        <IconButton
          color="primary"
          aria-label={t('details.zoomTo')}
          tooltipPlacement="top"
          disabled={!hasGeometry}
          onClick={onZoomIn}
          className="buttonOutline"
          size="small"
        >
          <ZoomInSearchIcon />
        </IconButton>
      </Box>
    </Box>
  );
});

export function FeatureInfo({ feature }: FeatureInfoProps): JSX.Element | null {
  logger.logTraceRender('components/details/feature-info', feature);

  // Hooks
  const theme = useTheme();
  const sxClasses = useMemo(() => getSxClasses(theme), [theme]);

  // State
  const [checked, setChecked] = useState<boolean>(false);

  // Store
  const checkedFeatures = useDetailsCheckedFeatures();
  const { addCheckedFeature, removeCheckedFeature } = useDetailsStoreActions();
  const { zoomToExtent, highlightBBox, addHighlightedFeature } = useMapStoreActions();
  const geochartLayerDataArrayBatch = useGeochartLayerDataArrayBatch();
  const { setSelectedLayerPath } = useGeochartStoreActions() ?? {};
  const geochartConfigs = useGeochartConfigs();

  // Use navigate hook for geochart (only if geochart state exists)
  const navigateToGeochart = useNavigateToTab(
    'geochart',
    setSelectedLayerPath ? (layerPath: string) => setSelectedLayerPath(layerPath) : undefined
  );

  // Feature data processing
  const featureData = useMemo(() => {
    if (!feature) return null;

    return {
      uid: feature.uid,
      iconSrc: feature.featureIcon,
      name:
        feature.nameField && feature.fieldInfo?.[feature.nameField]?.value
          ? (feature.fieldInfo?.[feature.nameField]?.value as string)
          : 'No name / Sans nom',
      extent: feature.extent,
      geometry: feature.geometry,
      geoviewLayerType: feature.geoviewLayerType,
    };
  }, [feature]);

  // Process feature info list
  const featureInfoList: TypeFieldEntry[] = useMemo(() => {
    if (!feature?.fieldInfo) return [];

    return Object.entries(feature.fieldInfo)
      .filter(([key]) => key !== feature.nameField)
      .map(([fieldName, field]) => ({
        fieldKey: field!.fieldKey,
        value: field!.value,
        dataType: field!.dataType,
        alias:
          feature.geoviewLayerType !== 'ogcWms' && feature.geoviewLayerType !== 'ogcWfs'
            ? field!.alias || fieldName
            : (field!.alias || fieldName).split('.').pop() || '',
        domain: null,
      }));
  }, [feature]);

  // Event Handlers
  const handleFeatureSelectedChange = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      if (!feature) return;

      if (!checked) {
        addCheckedFeature(feature);
      } else {
        removeCheckedFeature(feature);
      }
    },
    [addCheckedFeature, checked, feature, removeCheckedFeature]
  );

  const handleZoomIn = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      if (!featureData?.extent) return;

      // Buffer the extent to avoid zooming too close if it's a point
      const isPoint = featureData.geometry!.getType() === 'Point';
      const zoomExtent = isPoint ? GeoUtilities.bufferExtent(featureData.extent, EXTENT_BUFFER) : featureData.extent;

      // Zoom to extent and highlight the feature
      zoomToExtent(zoomExtent, { padding: ZOOM_PADDING, maxZoom: ZOOM_MAX_LEVEL })
        .then(() => {
          // Highlight the bounding box
          if (featureData.extent && !isPoint) {
            highlightBBox(featureData.extent, false);
          }
          // Add the current feature to highlights
          addHighlightedFeature(feature);
        })
        .catch((error: unknown) => {
          logger.logPromiseFailed('zoomToExtent in handleZoomIn in FeatureInfoNew', error);
        });
    },
    [featureData, feature, zoomToExtent, highlightBBox, addHighlightedFeature]
  );

  const handleGeochart = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      if (!feature) return;
      navigateToGeochart({ layerPath: feature.layerPath });
    },
    [feature, navigateToGeochart]
  );

  // Effects
  useEffect(() => {
    logger.logTraceUseEffect('FEATURE-INFO - checkedFeatures', checkedFeatures);

    if (!featureData?.uid) return;

    setChecked(checkedFeatures.some((checkedFeature) => checkedFeature?.uid === featureData.uid));
  }, [checkedFeatures, featureData]);

  // Early return if no feature
  if (!featureData) return null;

  // Check if layer has geochart config and data with features
  const hasGeochart =
    !!geochartConfigs?.[feature.layerPath] &&
    (geochartLayerDataArrayBatch?.some((entry) => entry.layerPath === feature.layerPath && (entry.features?.length ?? 0) > 0) ?? false);

  return (
    <Paper sx={PAPER_STYLES}>
      <FeatureHeader
        iconSrc={featureData.iconSrc}
        name={featureData.name}
        hasGeometry={!!featureData.geometry && !!featureData.extent && !featureData.extent.includes(Infinity)}
        hasGeochart={hasGeochart}
        checked={checked}
        onCheckChange={handleFeatureSelectedChange}
        onZoomIn={handleZoomIn}
        onGeochart={handleGeochart}
      />

      <Box sx={sxClasses.featureInfoListContainer}>
        <FeatureInfoTable featureInfoList={featureInfoList} />
      </Box>
    </Paper>
  );
}
